"use client";

import { useRouter } from "@bprogress/next/app";
import { Box } from "@mui/material";
import MuiLinkRoute from "@v2/components/MuiLinkRoute";
import { decodePath, parseSectionLink } from "@v2/helpers/hashScroll";
import { ChevronDown } from "lucide-react";
import { MouseEvent, useMemo } from "react";
import { requestPlpHeaderScroll } from "./scrollToPlpHeader";

export type PlpHeroQuickLink = { id: number; label: string; link: string };

export default function PlpHeroQuickLinks({
    chips,
    currentUrl,
}: {
    chips: PlpHeroQuickLink[];
    // Passed down rather than read with useSearchParams(), which would opt the page
    // out of server rendering and make the brand route return 500.
    currentUrl: string;
}) {
    const router = useRouter();
    const activeIndex = useMemo(() => findActiveChipIndex(chips, currentUrl), [chips, currentUrl]);
    const currentPath = useMemo(() => parseAddress(currentUrl)?.path ?? null, [currentUrl]);
    // A scrolling chip is lit only when it opens the row. Once something else stands
    // ahead of it - another list to switch to, or another section - it is one offer
    // among several, and a row of black chips says nothing at all.
    const leadsWithScroll = useMemo(
        () => !!chips.length && scrollsPage(chips[0].link, currentPath),
        [chips, currentPath],
    );

    const navigate = (link: string) => (event: MouseEvent<HTMLAnchorElement>) => {
        // A chip that names a section of this page has already been answered by the
        // site-wide listener, which sees every link there is.
        if (event.defaultPrevented) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        if (!link.startsWith("/")) return;

        event.preventDefault();
        const clickedFrom = decodeURI(`${window.location.pathname}${window.location.search}`);
        if (decodeURI(link) === clickedFrom) return;

        // A chip that only narrows the page it is already on leaves the shopper in
        // the hero looking at a list that changed below them, so the toolbar carries
        // the page down to it. A chip that leads somewhere else is a normal
        // navigation and starts that page at its own top — or, when it carries a
        // hash, at the section it names, which the page it lands on scrolls to.
        const staysOnPage = decodeURI(link).split("#")[0].split("?")[0] === window.location.pathname;
        if (!staysOnPage) {
            router.push(link);
            return;
        }

        requestPlpHeaderScroll();
        router.push(link, { scroll: false });
    };

    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {chips.map((chip, index) => {
                // A chip that scrolls the page it is already on is an invitation to
                // read further down, not a narrower list to switch to. It says so with
                // an arrow, and is lit only when it is the one that opens the row.
                const scrollsThisPage = scrollsPage(chip.link, currentPath);
                const active = (index === 0 && leadsWithScroll) || index === activeIndex;

                return (
                    <MuiLinkRoute
                        key={chip.id}
                        href={chip.link}
                        onClick={navigate(chip.link)}
                        underline="none"
                        aria-current={!scrollsThisPage && active ? "page" : undefined}
                        sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 0.75, minHeight: 48, bgcolor: active ? "grey.900" : "#fff", color: active ? "#fff" : "grey.900", border: "1px solid", borderColor: active ? "grey.900" : "grey.300", borderRadius: 1, px: { xs: 2, sm2: 2.5 }, py: 1.25, fontSize: 13, fontWeight: 700, textDecoration: "none", transition: "150ms", "&:hover": { bgcolor: active ? "grey.800" : "grey.50", borderColor: "grey.900", color: active ? "#fff" : "grey.900" } }}
                    >
                        {chip.label}
                        {/* Only the chip that scrolls this page says so. A chip that
                            leads somewhere else is a plain link, hash or no hash. */}
                        {scrollsThisPage && <ChevronDown size={16} color={active ? "#fff" : "#212121"} aria-hidden />}
                    </MuiLinkRoute>
                );
            })}
        </Box>
    );
}

// Whether a chip takes the reader down this same page rather than to another one.
// A link with no path of its own names the page being read.
function scrollsPage(link: string, currentPath: string | null) {
    const section = parseSectionLink(link);

    return !!section?.hash && (section.path === null || section.path === currentPath);
}

// The lit chip is the one that names the page being read. It has to hold for both
// shapes these links come in: a chip that narrows the current address with a query,
// and a chip that leads to a separate equivalent path one level down.
//
// A chip matches when it points at the same path and every parameter it carries is
// already set to the same value here. The page routinely carries more than the chip
// does - a sort, a page number - so the chip is allowed to be a subset, and the chip
// that pins down the most parameters wins. That is what keeps an "همه" chip lit on the
// bare list and hands the highlight over to a narrower chip beside it.
export function findActiveChipIndex(chips: PlpHeroQuickLink[], currentUrl: string): number {
    const current = parseAddress(currentUrl);
    if (!current) return -1;

    let activeIndex = -1;
    let activeScore = -1;

    chips.forEach((chip, index) => {
        const target = parseAddress(chip.link);
        if (!target || target.path !== current.path) return;
        // A chip that scrolls this page names a section of it, not the list it is
        // showing, so it never takes the highlight away from the chip that does.
        if (target.hash) return;

        const params = [...target.params.entries()];
        const matches = params.every(([name, value]) => current.params.getAll(name).includes(value));
        if (!matches || params.length <= activeScore) return;

        activeScore = params.length;
        activeIndex = index;
    });

    return activeIndex;
}

// Only site addresses take part: an external link never names this page, and the click
// handler above leaves those alone too. A bare `#section` names the page being read
// rather than another one, and is left to the click handler as well.
function parseAddress(url: string): { path: string; params: URLSearchParams; hash: string } | null {
    if (!url?.startsWith("/")) return null;

    const address = new URL(url, "http://vahdatoptic.local");
    return { path: decodePath(address.pathname), params: address.searchParams, hash: address.hash.slice(1) };
}
